import { Form, Select } from "antd";
import { ChevronDown, ChevronRight, Filter, Search } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { CustomSelect, InfiniteScrollSelect } from "~/components/common";
import { useDebounceValue } from "~/hooks/use-debounce";
import { useGetClients } from "~/services/client/client.queries";
import { useGetLocationsInfinite } from "~/services/common";
import {
  ClientStatus,
  InventoryStatusCode,
  InventoryType,
} from "~/utils/constants";
import { useInventoryStore } from "../inventory.store";

type Props = {
  totalItems?: number;
  currentItemsCount?: number;
};

export const FilterInventories: FC<Props> = ({ totalItems = 0, currentItemsCount = 0 }) => {
  const { filters, setFilters } = useInventoryStore();

  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [search] = useDebounceValue(searchTerm, 200);

  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });

  useEffect(() => {
    setFilters({ search, page: 1 });
  }, [search, setFilters]);

  const infiniteLocations = useGetLocationsInfinite();

  return (
    <div className="bg-white rounded-lg border p-4 mb-6 shadow-sm">
      <div className="flex flex-col md:flex-row justify-between space-y-4 md:space-y-0">
        <div className="flex flex-1 space-x-2">
          <div className="relative w-full md:w-64">
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
              placeholder="Search inventory..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Search
              className="absolute left-3 top-2.5 text-gray-400"
              size={16}
            />
          </div>

          <button
            className="px-3 py-2 border rounded-lg hover:bg-gray-50 flex items-center space-x-1"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            <span>Filters</span>
            {showFilters ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500">
            Showing {currentItemsCount} of {totalItems} items
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
          <div>
            <Form.Item label="Client" layout="vertical">
              <CustomSelect
                size="large"
                showSearch
                placeholder="Select a client"
                optionFilterProp="children"
                loading={clientsLoading}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[
                  { value: "", label: "All Clients" },
                  ...(clients?.items?.length
                    ? clients.items.map((client) => ({
                        value: client.id,
                        label: `${
                          client.companyName ||
                          [client.firstName, client.lastName]
                            .filter(Boolean)
                            .join(" ")
                            .trim() ||
                          "Unnamed Client"
                        } - ${client.email}`,
                      }))
                    : [
                        {
                          value: "",
                          label: "No active clients available",
                          disabled: true,
                        },
                      ]),
                ]}
                value={filters.clientId}
                onChange={(clientId) =>
                  setFilters({ clientId: clientId || "" })
                }
              />
            </Form.Item>
          </div>

          <div>
            <Form.Item label="Status" layout="vertical">
              <Select
                size="large"
                value={filters.status}
                onChange={(status) => setFilters({ status })}
                options={[
                  {
                    value: "",
                    label: "All Statuses",
                  },
                  {
                    value: InventoryStatusCode.NO_POSSESSION,
                    label: "Not Collected",
                  },
                  {
                    value: InventoryStatusCode.AVAILABLE_LIVE,
                    label: "Available - Live",
                  },
                  {
                    value: InventoryStatusCode.AVAILABLE_ON_HAND,
                    label: "Available - On Hand",
                  },
                  {
                    value: InventoryStatusCode.RELEASED,
                    label: "Released",
                  },
                ]}
              />
            </Form.Item>
          </div>

          <div>
            <Form.Item label="Location" layout="vertical">
              <InfiniteScrollSelect
                size="large"
                queryResult={infiniteLocations}
                getOptionLabel={(location) => location.name}
                getOptionValue={(location) => location.id}
                value={filters.locationId}
                onChange={(locationId) =>
                  setFilters({ locationId: locationId || "" })
                }
                allOptions={{ value: "", label: "All Locations" }}
              />
            </Form.Item>
          </div>

          <div>
            <Form.Item label="Type" layout="vertical">
              <Select
                size="large"
                value={filters.itemType}
                onChange={(itemType) => setFilters({ itemType })}
                options={[
                  { value: "", label: "All Types" },
                  { value: InventoryType.PACKAGE, label: "Containers" },
                  { value: InventoryType.PIECE, label: "Pieces" },
                  { value: InventoryType.PART, label: "Parts" },
                ]}
              />
            </Form.Item>
          </div>
        </div>
      )}
    </div>
  );
};
